import { TicketSalePageEvent } from 'app/pages/TicketSalePage/slice/types';
import { AxiosResponse } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { call } from 'redux-saga/effects';
import { takeEveryAsync } from 'saga-toolkit';
import { formatAddonsForAPI, formatTopUpsForAPI } from 'utils/addons';
import request from 'utils/request';
import {
  EVENT_TYPE_WITH_CALENDAR,
  EVENT_TYPE_WITH_SEATING_CHART,
} from 'utils/static/constants';
import { RequestOptions } from '../../types/RequestOptions';
import {
  addAddonsAndTopUpsOnOrder,
  fetchCartData,
  sendBillingAndCustomerInfo,
} from './index';
import {
  formatSelectedBundlesForReservationEndpoint,
  formatSelectedTicketsForReservationEndpoint,
  IFormattedBundleForReservation,
  IFormattedTicketForReservation,
} from './utils';
import {
  SelectedBundle,
  SelectedSeat,
  SelectedTicket,
} from 'types/SelectedItem';
import { FetchCartDataPayload } from './types';
import { VoucherInterface } from 'types/CartItem';

interface FetchCartDataRequestOptions extends RequestOptions {
  body: {
    tickets?: IFormattedTicketForReservation[];
    bundles?: IFormattedBundleForReservation[];
    order_id?: string;
    voucher?: string;
    hold_token?: string;
  };
}

export interface FetchCartDataArgs {
  event: TicketSalePageEvent;
  selectedTickets: (SelectedTicket | SelectedSeat)[];
  selectedBundles: SelectedBundle[];
  voucher: VoucherInterface;
  order_id: string;
  hold_token: string;
  queryParams: {
    preview: string | null;
  };
}

const getActionBody = ({
  event,
  selectedTickets,
  selectedBundles,
  voucher,
  order_id,
  hold_token,
}: FetchCartDataArgs) => {
  const body: FetchCartDataRequestOptions['body'] = {};

  if (selectedTickets.length) {
    body.tickets = formatSelectedTicketsForReservationEndpoint(
      selectedTickets,
      event,
    );
  }

  if (selectedBundles.length) {
    body.bundles = formatSelectedBundlesForReservationEndpoint(
      selectedBundles,
      event,
    );
  }

  if (voucher) {
    body.voucher = voucher.voucherCode.code;
  }

  if (order_id) {
    body.order_id = order_id;
  }

  if (hold_token) {
    body.hold_token = hold_token;
  }

  return body;
};

function* fetchCartDataSaga({
  meta: { arg },
}: {
  meta: {
    arg: FetchCartDataArgs;
  };
}): Generator<any, FetchCartDataPayload, unknown> {
  const body = getActionBody(arg);

  const requestOptionsForPostMethod: FetchCartDataRequestOptions = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body,
  };

  let response: AxiosResponse<FetchCartDataPayload['serverResponse']>;
  const { event, voucher, selectedTickets, selectedBundles } = arg;

  try {
    const result = yield call(
      request,
      `/events/${event.id}/${
        event.type === EVENT_TYPE_WITH_SEATING_CHART ? 'seats' : 'tickets'
      }?include=tickets,tickets.id,tickets.ticketTypeId${
        event.type === EVENT_TYPE_WITH_CALENDAR ? ',tickets.date' : ''
      }${selectedBundles.length ? ',tickets.bundleId' : ''}${
        arg.queryParams.preview ? '&preview=true' : ''
      }
      `,
      requestOptionsForPostMethod,
    );
    response = result as AxiosResponse<FetchCartDataPayload['serverResponse']>;
  } catch (error) {
    // quick fix: these lines of code cover the case when the order has a status different than 'cart'
    // todo: we should refactor the endpoint to create a new order when this situation happens
    delete requestOptionsForPostMethod.body.order_id;

    const result = yield call(
      request,
      `/events/${event.id}/${
        event.type === EVENT_TYPE_WITH_SEATING_CHART ? 'seats' : 'tickets'
      }?include=tickets,tickets.id,tickets.ticketTypeId${
        event.type === EVENT_TYPE_WITH_CALENDAR ? ',tickets.date' : ''
      }${selectedBundles.length ? ',tickets.bundleId' : ''}`,
      requestOptionsForPostMethod,
    );
    response = result as AxiosResponse<FetchCartDataPayload['serverResponse']>;
  }

  return {
    serverResponse: response.data,
    tickets: selectedTickets,
    bundles: selectedBundles,
    selectedEvent: event,
    voucher,
  };
}

interface AddonsRequestOptions extends RequestOptions {
  body: {
    order_id: string;
    addons?: any; // replace any with the correct type after the route is updated
    credit?: any; // replace any with the correct type after the route is updated
  };
}

function* addAddonsAndTopUpsOnOrderSaga({
  meta: {
    arg: {
      eventId,
      cartUUID,
      addons,
      topUps,
      tickets,
      ticketsData,
      queryParams,
    },
  },
}) {
  const requestOptionsForPostMethod: AddonsRequestOptions = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: {
      order_id: cartUUID,
    },
  };
  const responseData: { addons?: any; topUps?: any } = {};

  if (addons.length) {
    requestOptionsForPostMethod.body.addons = {
      ...formatAddonsForAPI(addons, tickets, ticketsData),
    };
    responseData.addons = addons;
  }

  const formattedTopUps = formatTopUpsForAPI(topUps);

  requestOptionsForPostMethod.body.credit = {
    ...formattedTopUps,
  };
  responseData.topUps = topUps;

  if (addons.length > 0 || !isEmpty(topUps)) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const response: AxiosResponse = yield call(
      request,
      `/events/${eventId}/attendee_forms${
        queryParams.preview ? '?preview=true' : ''
      }`,
      requestOptionsForPostMethod,
    );
  }

  return responseData;
}

function* sendBillingAndCustomerInfoSaga({ meta: { arg: body } }) {
  const options = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body,
  };

  // do we still need this?
  if (body.accessToken) {
    options.headers['Authorization'] = body.accessToken;
  }

  yield call(request, '/events/JP9zn9aK/tickets/checkout', options);

  return { body };
}

export function* cartSaga() {
  yield takeEveryAsync(fetchCartData.type, fetchCartDataSaga);
  yield takeEveryAsync(
    addAddonsAndTopUpsOnOrder.type,
    addAddonsAndTopUpsOnOrderSaga,
  );
  yield takeEveryAsync(
    sendBillingAndCustomerInfo.type,
    sendBillingAndCustomerInfoSaga,
  );
}
